import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, downloadReport, fmtErr, MODULE_PATHS } from "../lib/api";
import { useSite } from "../lib/site";
import { PageHeader } from "../components/ModulePage";
import { RagBadge } from "../components/Badges";
import { Button } from "../components/ui/button";

const Stat = ({ label, value, cls = "", testid }) => (
  <div className={`panel p-4 ${cls}`} data-testid={testid}>
    <p className="eyebrow">{label}</p>
    <p className="mt-2 font-mono text-4xl font-black">{value}</p>
  </div>
);

const ModuleDots = ({ modules, onPick }) => (
  <div className="flex flex-wrap gap-1">
    {modules.map((m) => (
      <button key={m.key} onClick={() => onPick(m.key)} title={`${m.name}: ${m.score}%`}
        className={`h-7 rounded px-2 font-mono text-[10px] uppercase ${{ red: "bg-red-500/20 text-red-300", amber: "bg-amber-500/20 text-amber-300", green: "bg-emerald-500/20 text-emerald-300" }[m.status]}`}>
        {m.name.split(" ")[0]}
      </button>
    ))}
  </div>
);

export default function GroupDashboard() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const { select } = useSite();
  const nav = useNavigate();
  useEffect(() => {
    api.get("/group-dashboard").then((r) => setD(r.data));
  }, []);
  const open = (id, path = "/") => { select(id); nav(path); };
  const pdf = async () => {
    setBusy(true);
    try { await downloadReport("group"); } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  if (!d) return <p className="text-slate-500" data-testid="group-loading">Loading group data...</p>;
  const g = d.group;
  const sites = [...d.sites].sort((a, b) => a.score - b.score);
  return (
    <div data-testid="group-dashboard-page">
      <PageHeader eyebrow={`Group view - ${d.sites.length} site(s)`} title="Group compliance dashboard" description="Every site you have access to, worst-performing first. Click a site to open its dashboard.">
        <Button onClick={pdf} disabled={busy} className="tap bg-rose-600 hover:bg-rose-700" data-testid="group-report-btn">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}Group PDF report
        </Button>
      </PageHeader>
      <div className="fade-up grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Group score" value={`${g.score}%`} testid="group-score" />
        <Stat label="Red items" value={g.totals?.red ?? 0} cls="border-l-4 border-red-500/50 text-red-400" testid="group-red" />
        <Stat label="Amber items" value={g.totals?.amber ?? 0} cls="border-l-4 border-amber-500/50 text-amber-400" testid="group-amber" />
        <Stat label="Green items" value={g.totals?.green ?? 0} cls="border-l-4 border-emerald-500/50 text-emerald-400" testid="group-green" />
        <Stat label="Overdue actions" value={g.categories?.overdue_actions ?? 0} testid="group-overdue-actions" />
        <Stat label="Overdue inspections" value={g.categories?.overdue_inspections ?? 0} testid="group-overdue-inspections" />
        <Stat label="Expired certificates" value={g.categories?.expired_certificates ?? 0} testid="group-expired-certs" />
        <Stat label="High risk open" value={g.kpis?.high_risk_open ?? 0} testid="group-high-risk" />
      </div>
      <h2 className="font-display mb-3 mt-8 text-2xl font-bold uppercase">Site league table</h2>
      <div className="space-y-3">
        {sites.map((s) => {
          const st = s.totals.red ? "red" : s.totals.amber ? "amber" : "green";
          return (
            <div key={s.id} className="panel grid gap-4 p-4 lg:grid-cols-[1.4fr_auto_1.6fr_auto] lg:items-center" data-testid={`group-site-${s.id}`}>
              <button onClick={() => open(s.id)} className="text-left" data-testid={`group-site-open-${s.id}`}>
                <p className="font-display text-xl font-bold uppercase hover:text-rose-300">{s.name}</p>
                <p className="eyebrow text-[10px]">{s.type || "Site"}</p>
              </button>
              <div className="flex items-center gap-3">
                <span className="font-mono text-3xl font-bold" data-testid={`group-site-score-${s.id}`}>{s.score}%</span>
                <RagBadge status={st} />
              </div>
              <ModuleDots modules={s.modules} onPick={(k) => open(s.id, MODULE_PATHS[k])} />
              <div className="grid grid-cols-4 gap-3 text-center font-mono text-xs">
                <span><span className="block text-lg text-red-400">{s.totals.red}</span>red</span>
                <span><span className="block text-lg text-amber-400">{s.totals.amber}</span>amber</span>
                <span><span className="block text-lg text-slate-100">{s.kpis.open_work_orders}</span>WOs</span>
                <span><span className="block text-lg text-slate-100">{s.kpis.open_incidents}</span>incid.</span>
              </div>
            </div>
          );
        })}
        {!sites.length && <p className="panel p-8 text-center text-sm text-slate-500">No sites yet.</p>}
      </div>
    </div>
  );
}
